/**
 * My agent — the adviser builds their own AI agent by talking to the iMaven Agent Builder (decided
 * 2026-10-20; Synthora Hub is now Power mode, switched on only from the CRM).
 *
 * Left: the conversation (type, drop documents, or press Talk for a live voice conversation — voice turns
 * land in the same conversation). Right: Proposals (approve / edit / dismiss, by chapter), Applied (undo),
 * Tests (simulated client + expert reviewer → fixes), Practice (what the builder knows about the adviser,
 * documents, completeness, which iMaven reference agent it compares with).
 *
 * Slow work (drafting proposals, reading documents, tests) runs in the background on the server and
 * reports into the conversation; this page polls while anything is running.
 *
 * API: Maven-platform-Server routes/agentBuilderRoutes.ts (/agent-builder/*).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';
import ProposalsPanel from './ProposalsPanel';
import TestsPanel from './TestsPanel';
import PracticePanel from './PracticePanel';
import LearnPanel from './LearnPanel';
import PublishPanel from './PublishPanel';
import InterviewPanel from './InterviewPanel';
import ResetDialog from './ResetDialog';
import useBuilderVoice from './useBuilderVoice';
import {
  Page, Head, Title, Subtitle, Layout, Card, CardBody, Tabs, Tab, Badge, PrimaryBtn, GhostBtn, SmallBtn, Row, Muted, Help,
  Pill, TextArea, Select, Bar, Wave, errText,
} from './styles';

const LANGS = [['en', 'English'], ['ru', 'Русский'], ['he', 'עברית'], ['fr', 'Français'], ['es', 'Español'], ['ar', 'العربية'], ['uk', 'Українська']];
const LANG_KEY = 'imaven_builder_lang';
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.md,.html,.png,.jpg,.jpeg,.webp,.tif,.tiff';

/** The adviser's choice, else the browser's language when the builder speaks it, else English. */
const initialLang = () => {
  let saved = '';
  try { saved = localStorage.getItem(LANG_KEY) || ''; } catch { /* private mode */ }
  const browser = String(navigator.language || 'en').slice(0, 2).toLowerCase();
  return saved || (LANGS.some(([k]) => k === browser) ? browser : 'en');
};

function Bubble({ m, onOpenGroup, onOpenTest }) {
  const mine = m.role === 'user';
  if (m.role === 'event') {
    const tokens = m.text === 'insufficient_tokens';
    return (
      <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 10, background: tokens ? 'rgba(248,81,73,0.1)' : 'rgba(47,129,247,0.08)', border: `1px solid ${tokens ? 'rgba(248,81,73,0.35)' : 'rgba(47,129,247,0.25)'}`, fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
        {tokens ? <span>You are out of AI tokens, so this could not finish. <a href="/settings/tokens" style={{ color: '#79c0ff' }}>Top up</a> and ask again.</span> : m.text}
        {(m.groupIds?.length > 0 || m.testRunId) && (
          <Row style={{ marginTop: 8 }}>
            {m.groupIds?.map(g => <SmallBtn key={g} onClick={() => onOpenGroup(g)}>Review proposals</SmallBtn>)}
            {m.testRunId && <SmallBtn onClick={() => onOpenTest(m.testRunId)}>Open the test</SmallBtn>}
          </Row>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', margin: '8px 0' }}>
      <div style={{ maxWidth: '86%', padding: '9px 12px', borderRadius: 12, background: mine ? '#1f6feb' : '#21262d', color: '#e6edf3', fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {m.via === 'voice' && <span title="said by voice" style={{ opacity: 0.6, marginRight: 4 }}>🎙</span>}
        {m.text}
        {!mine && (m.groupIds?.length > 0 || m.testRunId) && (
          <Row style={{ marginTop: 8 }}>
            {m.groupIds?.map(g => <SmallBtn key={g} onClick={() => onOpenGroup(g)}>Review proposals</SmallBtn>)}
            {m.testRunId && <SmallBtn onClick={() => onOpenTest(m.testRunId)}>Open the test</SmallBtn>}
          </Row>
        )}
      </div>
    </div>
  );
}

export default function AgentBuilderPage() {
  const api = useAuthenticatedAxios();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [applied, setApplied] = useState([]);
  const [tab, setTab] = useState('proposals');
  const [focusGroupId, setFocusGroupId] = useState(null);
  const [focusRunId, setFocusRunId] = useState(null);
  const [text, setText] = useState('');
  const [pending, setPending] = useState(null);   // the adviser's message while the builder answers
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lang, setLang] = useState(initialLang);
  const [loadError, setLoadError] = useState('');
  const kicked = useRef(false);
  const scroll = useRef(null);
  const fileInput = useRef(null);
  const seenEvents = useRef(new Set());
  const voiceRef = useRef(null);
  const [showReset, setShowReset] = useState(false);
  const [agentNow, setAgentNow] = useState(null);   // what the agent does after the last approval

  const loadProposals = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([api.get('/agent-builder/proposals?status=proposed'), api.get('/agent-builder/proposals?status=applied&limit=100')]);
      setGroups((p.data?.groups || []).reverse());
      setApplied((a.data?.groups || []).reverse());
    } catch { /* keep the last view */ }
  }, [api]);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/agent-builder');
      if (r.data?.language) setLang(r.data.language);
      setData(r.data);
      setLoadError('');
      // A background job just reported: refresh the cards.
      const fresh = (r.data?.messages || []).filter(m => m.role === 'event' && !seenEvents.current.has(m.id));
      if (fresh.length) {
        fresh.forEach(m => seenEvents.current.add(m.id));
        loadProposals();
        // A live voice conversation hears it too, so it never says "still preparing" about finished work.
        if (voiceRef.current?.active) fresh.forEach(m => voiceRef.current.notify(m.text === 'insufficient_tokens' ? 'The adviser is out of AI tokens; that work could not finish.' : m.text));
      }
      return r.data;
    } catch (e) {
      setLoadError(errText(e, 'Could not open the builder.'));
      return null;
    }
  }, [api, loadProposals]);

  const voice = useBuilderVoice(api, {
    language: lang,
    onToolResult: (r) => { if (r?.groupIds?.length || r?.acted) loadProposals(); if (r?.testRunId) { setFocusRunId(r.testRunId); } },
    onEnded: () => { load(); loadProposals(); },
  });
  voiceRef.current = voice;

  // First load; a first visit starts with the builder's welcome.
  useEffect(() => {
    (async () => {
      const d = await load();
      await loadProposals();
      (d?.messages || []).forEach(m => { if (m.role === 'event') seenEvents.current.add(m.id); });
      if (d && !kicked.current && (d.messages || []).length === 0) {
        kicked.current = true;
        setSending(true);
        try { await api.post('/agent-builder/turn', { kickoff: true, language: lang }); await load(); } catch { /* the page still works */ } finally { setSending(false); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while something runs in the background (drafts, documents, tests) or a voice conversation is on.
  const running = (data?.jobs || []).some(j => j.status === 'running');
  useEffect(() => {
    const ms = running || voice.active ? 4000 : 30000;
    const t = setInterval(() => { load(); }, ms);
    return () => clearInterval(t);
  }, [running, voice.active, load]);

  useEffect(() => { scroll.current?.scrollTo({ top: scroll.current.scrollHeight, behavior: 'smooth' }); }, [data?.messages?.length, pending, voice.caption]);

  const send = async (override) => {
    const t = (typeof override === 'string' ? override : text).trim();
    if (!t || sending) return;
    setText(''); setPending(t); setSending(true);
    try {
      const r = await api.post('/agent-builder/turn', { text: t });
      if (r.data?.groupIds?.length) loadProposals();
      if (r.data?.testRunId) { setFocusRunId(r.data.testRunId); }
      await load();
    } catch (e) {
      if (e?.response?.status !== 402) showToast(errText(e, 'The builder could not answer. Try again.'), 'error');
      setText(t);
    } finally { setPending(null); setSending(false); }
  };

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const maxMb = data?.limits?.maxFileMb || 20;
    const tooBig = list.find(f => f.size > maxMb * 1024 * 1024);
    if (tooBig) { showToast(`“${tooBig.name}” is larger than ${maxMb} MB.`, 'error'); return; }
    if (list.length > (data?.limits?.maxFiles || 5)) { showToast(`Up to ${data?.limits?.maxFiles || 5} files at a time.`, 'error'); return; }
    const fd = new FormData();
    list.forEach(f => fd.append('files', f));
    setUploading(true);
    try {
      const r = await api.post('/agent-builder/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const dup = (r.data?.jobs || []).filter(j => j.duplicate).map(j => j.name);
      showToast(`Reading ${list.length} document${list.length === 1 ? '' : 's'}… The file${list.length === 1 ? ' is' : 's are'} not kept — only knowledge, never personal details.${dup.length ? ` (Read before: ${dup.join(', ')})` : ''}`, 'success');
      await load();
    } catch (e) { showToast(errText(e, 'Upload failed'), 'error'); } finally { setUploading(false); if (fileInput.current) fileInput.current.value = ''; }
  };

  const changeLang = async (v) => {
    setLang(v);
    try { localStorage.setItem(LANG_KEY, v); } catch { /* optional */ }
    try { await api.put('/agent-builder/settings', { language: v }); } catch { /* next turn sends it anyway */ }
  };

  const openGroup = (g) => {
    const inApplied = applied.some(x => x.groupId === g) && !groups.some(x => x.groupId === g);
    setTab(inApplied ? 'applied' : 'proposals');
    setFocusGroupId(g);
    setTimeout(() => document.getElementById(`group-${g}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  const openTest = (id) => { setTab('tests'); setFocusRunId(id); };

  const waiting = useMemo(() => groups.reduce((n, g) => n + g.proposals.filter(p => ['proposed', 'conflict', 'failed'].includes(p.status)).length, 0), [groups]);
  const jobs = (data?.jobs || []).filter(j => j.status === 'running');
  const score = data?.readiness?.score ?? 0;

  if (loadError && !data) {
    return <Page><Title>My agent</Title><Subtitle style={{ color: '#ff7b72' }}>{loadError}</Subtitle></Page>;
  }

  return (
    <Page
      onDragOver={e => { e.preventDefault(); }}
      onDrop={e => { e.preventDefault(); if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files); }}
    >
      <Head>
        <div>
          <Title>My agent</Title>
          <Subtitle>Build your AI agent together with the iMaven Agent Builder: tell it about your practice, share documents, approve what it proposes, and test it. Nothing changes in your agent without your approval.</Subtitle>
        </div>
        <Row>
          <div style={{ minWidth: 180 }}>
            <Row style={{ justifyContent: 'space-between' }}><Muted>Agent completeness</Muted><Muted>{score}%</Muted></Row>
            <Bar $pct={score} style={{ marginTop: 4 }}><div /></Bar>
          </div>
          <Select value={lang} onChange={e => changeLang(e.target.value)} title="The builder's language">
            {LANGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
          <GhostBtn onClick={() => navigate('/agent/history')}>History</GhostBtn>
          <GhostBtn onClick={() => setShowReset(true)} style={{ color: '#ff7b72', borderColor: 'rgba(248,81,73,0.4)' }}>Reset…</GhostBtn>
          {data?.powerMode && <GhostBtn onClick={() => navigate('/Synthora')}>Power mode</GhostBtn>}
        </Row>
      </Head>

      <Layout>
        <Card>
          <div ref={scroll} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', minHeight: 240 }}>
            {(data?.messages || []).map(m => <Bubble key={m.id} m={m} onOpenGroup={openGroup} onOpenTest={openTest} />)}
            {pending && <Bubble m={{ role: 'user', text: pending }} onOpenGroup={openGroup} onOpenTest={openTest} />}
            {sending && <Muted style={{ display: 'block', margin: '8px 0' }}>The builder is thinking…</Muted>}
            {jobs.map(j => <Muted key={j.id} style={{ display: 'block', margin: '6px 0' }}>⏳ {j.kind === 'document' ? 'Reading' : j.kind === 'test' ? 'Testing' : 'Preparing'}: {j.label}</Muted>)}
            {voice.active && voice.caption && <Bubble m={{ role: 'assistant', text: voice.caption, via: 'voice' }} onOpenGroup={openGroup} onOpenTest={openTest} />}
          </div>

          {voice.active && (
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px 16px' }}>
              <Row style={{ justifyContent: 'space-between' }}>
                <Row>
                  <Wave $phase={voice.phase}>{Array.from({ length: 14 }, (_, i) => <span key={i} style={{ animationDelay: `${i * 0.07}s` }} />)}</Wave>
                  <Muted>
                    {voice.phase === 'connecting' ? 'Connecting your microphone…' : voice.working ? 'Working on it…' : voice.phase === 'speaking' ? 'The builder is speaking' : voice.phase === 'thinking' ? 'Thinking…' : voice.muted ? 'Microphone muted' : 'Listening — you can interrupt'}
                    {voice.minutesLeft != null ? ` · ~${voice.minutesLeft} min left` : ''}
                  </Muted>
                </Row>
                <Row>
                  <SmallBtn onClick={voice.toggleMute} disabled={voice.phase === 'connecting'}>{voice.muted ? 'Unmute' : 'Mute'}</SmallBtn>
                  <SmallBtn onClick={voice.end} style={{ color: '#ff7b72' }}>{voice.phase === 'connecting' ? 'Cancel' : 'End'}</SmallBtn>
                </Row>
              </Row>
            </div>
          )}
          {voice.error && <Help style={{ color: '#ff7b72', padding: '0 16px' }}>{voice.error} <SmallBtn onClick={() => voice.setError('')} style={{ marginLeft: 6 }}>OK</SmallBtn></Help>}

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: 12 }}>
            <TextArea
              rows={2}
              value={text}
              disabled={voice.active}
              placeholder={voice.active ? 'End the voice conversation to type' : 'Tell the builder about your practice, ask what to improve, or drop documents here…'}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            />
            <Row style={{ marginTop: 8, justifyContent: 'space-between' }}>
              <Row>
                <input ref={fileInput} type="file" multiple accept={ACCEPT} style={{ display: 'none' }} onChange={e => upload(e.target.files)} />
                <GhostBtn disabled={uploading || voice.active} onClick={() => fileInput.current?.click()}>{uploading ? 'Uploading…' : '📎 Documents'}</GhostBtn>
                {!voice.active && data?.voice?.available && <GhostBtn onClick={() => voice.start()} title={`Live voice · about $${Number(data.voice.usdPerMinute || 0).toFixed(2)} per minute from your AI tokens`}>🎙 Talk</GhostBtn>}
              </Row>
              <PrimaryBtn disabled={sending || !text.trim() || voice.active} onClick={send}>Send</PrimaryBtn>
            </Row>
          </div>
        </Card>

        <Card>
          <Tabs>
            <Tab $on={tab === 'proposals'} onClick={() => setTab('proposals')}>Proposals{waiting > 0 && <Badge>{waiting}</Badge>}</Tab>
            <Tab $on={tab === 'applied'} onClick={() => setTab('applied')}>Applied</Tab>
            <Tab $on={tab === 'interview'} onClick={() => setTab('interview')}>Interview</Tab>
            <Tab $on={tab === 'tests'} onClick={() => setTab('tests')}>Tests</Tab>
            <Tab $on={tab === 'learn'} onClick={() => setTab('learn')}>Learn</Tab>
            <Tab $on={tab === 'publish'} onClick={() => setTab('publish')}>Publish</Tab>
            <Tab $on={tab === 'practice'} onClick={() => setTab('practice')}>Your practice</Tab>
          </Tabs>
          <CardBody>
            {agentNow && (tab === 'proposals' || tab === 'applied') && (
              <div style={{ background: 'rgba(63,185,80,0.08)', border: '1px solid rgba(63,185,80,0.35)', borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, lineHeight: 1.55 }}>
                <Row style={{ justifyContent: 'space-between' }}>
                  <b>Your agent now — checked after your approval</b>
                  <SmallBtn onClick={() => setAgentNow(null)}>✕</SmallBtn>
                </Row>
                <div style={{ color: '#8b98a5', margin: '2px 0 6px' }}>Up to {agentNow.questionLimit} questions per conversation · {agentNow.pathways} route{agentNow.pathways === 1 ? '' : 's'}{agentNow.mode === 'fixed' ? ' · your fixed questions' : ''}. A new client is asked first:</div>
                {(agentNow.firstQuestions || []).length ? <ol style={{ margin: 0, paddingLeft: 18 }}>{agentNow.firstQuestions.map((q, i) => <li key={i}>{q}</li>)}</ol> : <Muted>No questions yet — add routes or interview questions.</Muted>}
              </div>
            )}
            {tab === 'proposals' && <ProposalsPanel api={api} groups={groups} applied={applied} view="proposed" reload={async () => { await loadProposals(); await load(); }} focusGroupId={focusGroupId} onApplied={setAgentNow} />}
            {tab === 'applied' && <ProposalsPanel api={api} groups={groups} applied={applied} view="applied" reload={async () => { await loadProposals(); await load(); }} focusGroupId={focusGroupId} />}
            {tab === 'interview' && <InterviewPanel api={api} hasRoutes={(data?.overview?.pathways || []).length > 0} onSaved={load} />}
            {tab === 'tests' && <TestsPanel api={api} pathways={data?.overview?.pathways || []} hasWidget={!!data?.hasWidget} focusRunId={focusRunId} onChanged={load} />}
            {tab === 'learn' && <LearnPanel api={api} onStarted={load} />}
            {tab === 'publish' && <PublishPanel api={api} onAsk={(t) => send(t)} onOpenTests={() => setTab('tests')} />}
            {tab === 'practice' && <PracticePanel api={api} data={data} reload={load} />}
            {data?.reference && tab === 'proposals' && (
              <Help style={{ marginTop: 12 }}>
                Compared with <Pill>{data.reference.name}</Pill> — {data.gaps?.missing || 0} item(s) it has that your agent does not. Ask the builder to walk you through them.
              </Help>
            )}
          </CardBody>
        </Card>
      </Layout>
      {showReset && <ResetDialog api={api} onClose={() => setShowReset(false)} onDone={async () => { setAgentNow(null); await loadProposals(); await load(); }} />}
    </Page>
  );
}
